import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import { nanoid } from 'nanoid'
import { useRouter } from 'next/router'
import { toast } from 'sonner'
import useSWR, { useSWRConfig } from 'swr'
import Cookies from 'universal-cookie'
import getQueryString from '@/utils/query-string'
import { sessionStorageGet, sessionStorageRemove, sessionStorageSet } from '@/utils/storage'
import { getToken } from '@/utils/token'
import { googleLogin } from '@/data/api/auth'
import { getPendingInvite } from '@/data/api/invite'
import { getPendingProjectInvite } from '@/data/api/project-invite'
import { updateTeam } from '@/data/api/team'
import { getMe, removeMe, updateMe } from '@/data/api/user'
import { removeUserId, setUserId } from '@/data/cache/user'
import type { TCredit, TProCategory, TUserPrivateInfo } from '@/interfaces/user'
import type { CredentialResponse } from 'google-one-tap'
import type { ParsedUrlQuery } from 'querystring'
import { isProd, LOGIN_TOKEN_KEY } from '@/config'

const cookies = new Cookies()

async function fetcher(forceReload = false) {
  const result = await getMe({ forceReload })
  if (!result) {
    return
  }
  if ('error' in result) {
    return Promise.reject(result.error)
  }
  setUserId(result.user.id)
  return result
}

export default function useAuth() {
  const router = useRouter()
  const {
    data, error, isLoading, mutate,
  } = useSWR('/api/user/me', () => fetcher(), {
    revalidateIfStale: false,
    errorRetryCount: 1,
    shouldRetryOnError: false,
  })
  const { mutate: mutateGlobal } = useSWRConfig()
  const [ hasToken, setHasToken ] = useState(false)

  const mutateUserCache = useCallback((username: string | undefined, user: TUserPrivateInfo) => {
    mutateGlobal([ '/api/user', { key: 'username', value: username }], user, { revalidate: false })
  }, [ mutateGlobal ])

  const checkMe = useCallback((username = '') => {
    if (!username || !data?.user) {
      return false
    }
    return data?.user.username === username
  }, [ data ])

  const onLogined = useCallback(async (token: string, query?: ParsedUrlQuery, redirect = true) => {
    cookies.set(LOGIN_TOKEN_KEY, token, {
      path: '/',
      // サーバーの TOKEN_EXPIRES（prod: 1y / それ以外: 7d）に合わせる
      maxAge: isProd ? 60 * 60 * 24 * 365 : 60 * 60 * 24 * 7,
    })
    localStorage.setItem(LOGIN_TOKEN_KEY, token)
    const me = await fetcher(true)
    mutate(me)
    sessionStorageRemove('signup')
    // return_to が指定されていれば最優先で遵守する（招待フローなど）
    if (redirect && query?.return_to) {
      router.push(`${query.return_to}`)
      return
    }
    // Check for pending team invitations
    const pendingInviteResult = await getPendingInvite()
    if (pendingInviteResult && !('error' in pendingInviteResult) && pendingInviteResult.invite) {
      router.push(`/invite/${pendingInviteResult.invite.id}`)
      return
    }
    // Check for pending project invitations
    const pendingProjectInviteResult = await getPendingProjectInvite()
    if (
      pendingProjectInviteResult
      && !('error' in pendingProjectInviteResult)
      && pendingProjectInviteResult.invite
    ) {
      router.push(`/project-invite/${pendingProjectInviteResult.invite.id}`)
      return
    }
    if (!redirect) {
      return
    }
    if (query?.ref === 'pro') {
      router.push('/register-pro/bio')
      return
    }
    if (query?.user_id) {
      router.push(`/user/${query.user_id}`)
      return
    }
    router.push('/')
  }, [ mutate, router ])

  const logout = useCallback(() => {
    cookies.remove(LOGIN_TOKEN_KEY, { path: '/' })
    localStorage.removeItem(LOGIN_TOKEN_KEY)
    sessionStorageRemove('auth')
    removeUserId()
    window.location.href = '/'
  }, [])

  const login = useCallback(async (
    token: string,
    isExist: boolean,
    query: ParsedUrlQuery,
    redirect = true,
  ) => {
    if (!isExist) {
      sessionStorageSet('signup', {
        email: sessionStorageGet('auth-email'),
        token,
      })
      sessionStorageRemove('auth-email')
      sessionStorageRemove('email-verify')
      if (!redirect) {
        return
      }
      router.push(`/register${getQueryString(query)}`)
      return
    }
    sessionStorageRemove('auth-email')
    sessionStorageRemove('email-verify')
    await onLogined(token, query, redirect)
  }, [ onLogined, router ])

  const handleGoogleResponse = useCallback(async (
    response: CredentialResponse,
    query?: ParsedUrlQuery,
  ) => {
    const result = await googleLogin(response.credential)
    if ('error' in result) {
      toast.error(result.error || 'エラーが発生しました')
      // eslint-disable-next-line  no-console
      console.error(result.error)
      return Promise.reject(result.error)
    }
    // if user is not registered
    if (!result.registered) {
      sessionStorageSet('signup', result)
      router.push((`/register${getQueryString(query)}`))
      return
    }
    await onLogined(result.token, query)
  }, [ onLogined, router ])

  const removeUser = useCallback(async () => {
    const result = await removeMe()
    if ('error' in result) {
      return { error: result.error }
    }
    logout()
    return { success: true }
  }, [ logout ])

  const updateUser = useCallback(async (updates: Partial<TUserPrivateInfo>) => {
    const result = await updateMe(updates)
    if ('error' in result) {
      return { error: result.error }
    }
    const updatedMe = {
      ...data,
      user: {
        ...data?.user,
        ...updates,
      } as TUserPrivateInfo,
    }
    // updateMe のレスポンスでは mongoDB の populate しているプロパティが入っていない場合があったので、
    // 再度 fetcher を呼んでその返り値を mutate に渡す
    // const me = await fetcher()
    mutate(updatedMe, { revalidate: false })
    mutateUserCache(data?.user.username, updatedMe.user)
    return { success: true }
  }, [ mutate, mutateUserCache, data ])

  const updateService = useCallback(async (updates: TProCategory) => {
    if (!data?.user) {
      return
    }
    // ロールバック用に変更前のスナップショットを保持
    const prev = data
    const oldCategory = data.user.categories || []
    const updatedService = updates.id
      ? data?.user.categories.map((v) => (v.id === updates.id ? updates : v))
      : [{ ...updates, id: updates.id || nanoid() }, ...oldCategory ] // @todo add id server side
    const updatedUser = {
      ...data.user,
      categories: updatedService,
    } as TUserPrivateInfo
    const updatedMe = {
      ...data,
      user: updatedUser,
    }
    // 楽観的更新：サーバーレスポンス（Algolia/Mailchimp 同期で数秒かかる）を待たずに即時反映
    mutate(updatedMe, { revalidate: false })
    mutateUserCache(data.user.username, updatedUser)

    // 保存はバックグラウンドで実行し、失敗時のみロールバック
    const rollback = () => {
      mutate(prev, { revalidate: false })
      mutateUserCache(prev.user.username, prev.user)
      toast.error('サービスの保存に失敗しました')
    }
    updateMe({ categories: updatedService })
      .then((result) => {
        if (!result || 'error' in result) {
          rollback()
        }
      })
      .catch(rollback)
    return { success: true }
  }, [ data, mutate, mutateUserCache ])

  const removeService = useCallback(async (removeServiceId: string) => {
    if (!data?.user.categories || data.user.categories.length === 0) {
      return { success: false }
    }
    // ロールバック用に変更前のスナップショットを保持
    const prev = data
    const newCategories = data.user.categories
      .filter((v) => v.id !== removeServiceId)
    const updatedUser = {
      ...data.user,
      categories: newCategories,
    } as TUserPrivateInfo
    const updatedMe = {
      ...data,
      user: updatedUser,
    }
    // 楽観的更新：サーバーレスポンスを待たずに即時反映
    mutate(updatedMe, { revalidate: false })
    mutateUserCache(data.user.username, updatedUser)

    // 保存はバックグラウンドで実行し、失敗時のみロールバック
    const rollback = () => {
      mutate(prev, { revalidate: false })
      mutateUserCache(prev.user.username, prev.user)
      toast.error('サービスの削除に失敗しました')
    }
    updateMe({ categories: newCategories })
      .then((result) => {
        if (!result || 'error' in result) {
          rollback()
        }
      })
      .catch(rollback)
    return { success: true }
  }, [ data, mutate, mutateUserCache ])

  const updateWorks = useCallback(async (updates: TCredit) => {
    if (!data?.user) {
      return
    }
    // ロールバック用に変更前のスナップショットを保持
    const prev = data
    const oldCredits = data.user.credits || []
    const updatedWorks = updates.id
      ? data?.user.credits.map((v) => (v.id === updates.id ? updates : v))
      : [{ ...updates, id: updates.id || nanoid() }, ...oldCredits ] // @todo add id server side
    const updatedUser = {
      ...data.user,
      credits: updatedWorks,
    } as TUserPrivateInfo
    const updatedMe = {
      ...data,
      user: updatedUser,
    }
    // 楽観的更新：サーバーレスポンスを待たずに即時反映
    mutate(updatedMe, { revalidate: false })
    mutateUserCache(data.user.username, updatedUser)

    // 保存はバックグラウンドで実行し、失敗時のみロールバック
    const rollback = () => {
      mutate(prev, { revalidate: false })
      mutateUserCache(prev.user.username, prev.user)
      toast.error('実績の保存に失敗しました')
    }
    updateMe({ credits: updatedWorks })
      .then((result) => {
        if (!result || 'error' in result) {
          rollback()
        }
      })
      .catch(rollback)
    return { success: true }
  }, [ data, mutate, mutateUserCache ])

  const removeWorks = useCallback(async (removeCreditId: string) => {
    if (!data?.user.credits || data.user.credits.length === 0) {
      return { success: false }
    }
    // ロールバック用に変更前のスナップショットを保持
    const prev = data
    const newCredits = data.user.credits
      .filter((v) => v.id !== removeCreditId)
    const updatedUser = {
      ...data.user,
      credits: newCredits,
    } as TUserPrivateInfo
    const updatedMe = {
      ...data,
      user: updatedUser,
    }
    // 楽観的更新：サーバーレスポンスを待たずに即時反映
    mutate(updatedMe, { revalidate: false })
    mutateUserCache(data.user.username, updatedUser)

    // 保存はバックグラウンドで実行し、失敗時のみロールバック
    const rollback = () => {
      mutate(prev, { revalidate: false })
      mutateUserCache(prev.user.username, prev.user)
      toast.error('実績の削除に失敗しました')
    }
    updateMe({ credits: newCredits })
      .then((result) => {
        if (!result || 'error' in result) {
          rollback()
        }
      })
      .catch(rollback)
    return { success: true }
  }, [ data, mutate, mutateUserCache ])

  const removeTeamMember = useCallback(async (removeMemberId: string) => {
    if (!data?.user.children || data.user.children.length === 0) {
      return { success: false }
    }
    const newChildren = data.user.children.filter((v) => v.id !== removeMemberId).map((v) => v.id)
    const result = await updateTeam(newChildren, removeMemberId)
    const me = await fetcher(true)
    mutate(me, { revalidate: false })
    return result
  }, [ data?.user.children, mutate ])

  const reloadUser = useCallback(async () => {
    const result = await fetcher(true)
    mutate(result)
  }, [ mutate ])

  useEffect(() => {
    const token = getToken()
    setHasToken(!!token)
  }, [])

  return {
    isLoading,
    userId: data?.user.id || null,
    currentUser: error ? null : (data?.user || null),
    professionalReview: error ? null : (data?.professionalReview || null),
    isLoggedin: !error && !!data?.user,
    maybeLoggedin: (isLoading && hasToken),
    checkMe,
    logout,
    login,
    handleGoogleResponse,
    onLogined,
    updateUser,
    removeUser,
    reloadUser,
    removeTeamMember,
    updateService,
    removeService,
    updateWorks,
    removeWorks,
  }
}
